/**
 * EntityLinkView — React NodeView for the inline entityLink node ([[ ... ]]).
 *
 * Renders an entity-type icon + label as a clickable chip that opens the
 * entity preview modal (or navigates, for non-previewable types like tags).
 * A task link's icon shows whether the task is done (#324).
 */

import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useEntityPreview } from '../../contexts/EntityPreviewContext';
import { useTaskStatus } from '../../lib/taskStatusCache';
import { getEntityIcon, getEntityColor, type EntityType } from '../EntityChip';
import { TASK_ICONS, taskIconKind } from '../taskIcon';

export function EntityLinkView({ node }: NodeViewProps) {
  const { entityType, entityId, label } = node.attrs as {
    entityType: EntityType; entityId: string; label: string;
  };
  const { openPreview } = useEntityPreview();
  const isTask = entityType === 'task';
  const status = useTaskStatus(isTask ? entityId : null);
  const taskKind = taskIconKind(status);
  const Icon = isTask ? TASK_ICONS[taskKind] : getEntityIcon(entityType);
  const color = getEntityColor(entityType);
  const statusNote = isTask && status ? ` (${status.replace(/_/g, ' ')})` : '';

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (entityType && entityId) openPreview(entityType, entityId, label);
  }

  return (
    <NodeViewWrapper as="span" className={`entity-link-node entity-link-${entityType}`}>
      <button type="button" className="entity-link-chip" onClick={handleClick}
        contentEditable={false} aria-label={`Open ${entityType}${statusNote}: ${label || 'linked item'}`}>
        <Icon size={12} style={{ color }} data-task-icon={isTask ? taskKind : undefined} />
        <span className="entity-link-label">{label || 'Linked item'}</span>
      </button>
    </NodeViewWrapper>
  );
}
